"use client"

import { useCallback, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type { AIBillingMode } from "@/lib/ai/langchain/types"

export interface TenantUpdatePatch {
  phone_number?: string | null
  name?: string
  timezone?: string
  uses_dst?: boolean
  working_hours_start?: string
  working_hours_end?: string
  disable_free_plan_expiry?: boolean
  /** Partner program override; null follows the platform switch. */
  partner_program_enabled?: boolean | null
  is_active?: boolean
  conversation_quality_platform_enabled?: boolean
  conversation_quality_billing_mode?: AIBillingMode
}

export interface UpdatedTenant {
  phone_number?: string | null
  id: string
  name?: string
  logo_url?: string | null
  timezone?: string
  uses_dst?: boolean
  working_hours_start?: string
  working_hours_end?: string
  disable_free_plan_expiry?: boolean
  /** Partner program override; null follows the platform switch. */
  partner_program_enabled?: boolean | null
  is_active?: boolean
  conversation_quality_platform_enabled?: boolean
  conversation_quality_billing_mode?: AIBillingMode
}

/**
 * Mutation surface for the `/api/tenants/[id]` PATCH endpoint + the
 * `/api/tenants/[id]/logo` POST/DELETE endpoints.
 *
 * The hook owns the in-flight flags + last error; callers wire success
 * results into their own UI state (no implicit callbacks).
 *
 * Used by `TenantEditDialog` (super_user admin surface) and the
 * Organization-page form (owner self-serve). The endpoints enforce the
 * owner-or-super-user gate server-side, so this hook is just a thin
 * fetch wrapper.
 */
export function useUpdateTenant() {
  const { fetchWithAuth } = useAuth()
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetError = useCallback(() => setError(null), [])

  const updateTenant = useCallback(
    async (tenantId: string, patch: TenantUpdatePatch): Promise<UpdatedTenant> => {
      setIsSaving(true)
      setError(null)
      try {
        const res = await fetchWithAuth(`/api/tenants/${tenantId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error || "Failed to update organization")
        }
        const data = await res.json()
        return { id: tenantId, ...data.tenant }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error"
        setError(message)
        throw err
      } finally {
        setIsSaving(false)
      }
    },
    [fetchWithAuth],
  )

  const uploadLogo = useCallback(
    async (tenantId: string, file: File): Promise<{ url: string }> => {
      setIsUploadingLogo(true)
      setError(null)
      try {
        const formData = new FormData()
        formData.append("file", file)
        const res = await fetchWithAuth(`/api/tenants/${tenantId}/logo`, {
          method: "POST",
          body: formData,
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error || "Logo upload failed")
        }
        return await res.json()
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error"
        setError(message)
        throw err
      } finally {
        setIsUploadingLogo(false)
      }
    },
    [fetchWithAuth],
  )

  const removeLogo = useCallback(
    async (tenantId: string): Promise<void> => {
      setIsUploadingLogo(true)
      setError(null)
      try {
        const res = await fetchWithAuth(`/api/tenants/${tenantId}/logo`, {
          method: "DELETE",
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error || "Logo remove failed")
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error"
        setError(message)
        throw err
      } finally {
        setIsUploadingLogo(false)
      }
    },
    [fetchWithAuth],
  )

  return {
    isSaving,
    isUploadingLogo,
    error,
    updateTenant,
    uploadLogo,
    removeLogo,
    resetError,
  }
}
